import {groupByUniqueKey} from '@benchling/js-utils';

import {assertAndContinue} from 'Asserts';
import {BulkAssemblyBinType, BulkAssemblyProcedure} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {isEmptyBAValue, isBAId} from 'molbio/bulk-assembly/BulkAssemblyValues';

/**
 * Runtime validation of any constraints expected of BulkAssemblyProcedure that can't be cleanly
 * expressed in the type system.
 */
export function integrityCheckBulkAssemblyState(state: BulkAssemblyProcedure): void {
  function check(condition: boolean, message?: string): boolean {
    return assertAndContinue(
      condition,
      `Integrity error when updating bulk assembly state.${message ? ` ${message}` : ''}`,
      {state}
    );
  }

  const {binCidOrdering, binsByCid, fragments, constructs} = state;
  check(binCidOrdering.length === Object.keys(binsByCid).length);
  for (const binCid of binCidOrdering) {
    check(binsByCid.hasOwnProperty(binCid));
    check(binsByCid[binCid].cid === binCid);
  }
  const fragmentBinCids = new Set(
    Object.values(binsByCid).flatMap((bin) => (bin.binType === BulkAssemblyBinType.FRAGMENT ? [bin.cid] : []))
  );
  const fragmentsByCid = groupByUniqueKey(fragments, (fragment) => fragment.cid);
  for (const fragment of fragments) {
    if (!isBAId(fragment.sequence)) {
      check(fragment.sequenceVersionDbId == null, 'Version may only be specified for a resolved sequence.');
    }
    if (!isBAId(fragment.protein)) {
      check(fragment.proteinVersionDbId == null, 'Version may only be specified for a resolved protein.');
    }
    if (fragment.sequenceVersionDbId == null && fragment.proteinVersionDbId == null) {
      check(
        isEmptyBAValue(fragment.start) && isEmptyBAValue(fragment.end),
        'Start and end may only be specified in the context of a version.'
      );
    }
    if (isBAId(fragment.bin)) {
      check(fragmentBinCids.has(fragment.bin.id), "A fragment's bin must be a valid fragment bin.");
    }
  }
  for (const construct of constructs) {
    for (const [binCid, constructFragment] of Object.entries(construct.constructFragmentsByBinId)) {
      check(fragmentBinCids.has(binCid), 'Construct data must reference valid fragment bins.');
      if (isBAId(constructFragment.fragment)) {
        if (check(fragmentsByCid.hasOwnProperty(constructFragment.fragment.id))) {
          const fragment = fragmentsByCid[constructFragment.fragment.id];
          check(isBAId(fragment.bin) && fragment.bin.id === binCid);
        }
      }
    }
  }
}
